import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Deal-box container                                                 */
/*  A rounded white card with a decorative row of coloured "tag"       */
/*  chips at the top (inspired by the reference DEAL card).            */
/*  Every section on this page is wrapped in one of these boxes so     */
/*  that the SEO content sits on its own "card" and reads cleanly on   */
/*  mobile.                                                            */
/* ------------------------------------------------------------------ */
function DealBox({ title, children, id }) {
  return (
    <section
      id={id}
      className="relative bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm px-4 sm:px-5 pt-6 pb-5 mt-6"
    >
      {/* Decorative hanging tags */}
      <div
        aria-hidden="true"
        className="absolute -top-3 left-4 flex gap-1.5 select-none"
      >
        <span className="w-3 h-6 rounded-b-md bg-emerald-500 shadow-sm" />
        <span className="w-3 h-6 rounded-b-md bg-sky-500 shadow-sm" />
        <span className="w-3 h-6 rounded-b-md bg-rose-500 shadow-sm" />
        <span className="w-3 h-6 rounded-b-md bg-amber-500 shadow-sm" />
      </div>

      {title && (
        <h2 className="font-heading font-black text-slate-900 text-lg sm:text-xl tracking-tight border-b-2 border-orange-400 pb-2 mb-3">
          {title}
        </h2>
      )}
      <div className="text-[15px] text-slate-700 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  FAQ list                                                           */
/* ------------------------------------------------------------------ */
const FAQS = [
  {
    q: "1. What is the result of Morning Teer?",
    a: "The Morning Teer result is announced after the archers finish shooting arrows at the target. Officials then count each arrow and the last two digits of the total become the winning number. Two rounds are played every day — F/R around 10:30 AM and S/R around 11:30 AM — and both numbers together form the Morning Teer Result of that day.",
  },
  {
    q: "2. What is Morning Sunday Teer?",
    a: "Morning Sunday Teer refers to the Sunday edition of the traditional archery-based Shillong Morning Teer game. If a Sunday draw is conducted, the Morning Sunday Teer Result is announced the same way — archers shoot arrows and the last two digits of the total arrow count become the winning number for that round.",
  },
  {
    q: "3. How is Shillong Morning Teer different from other games?",
    a: "In most online lottery games the winning number is generated by a computer or software. In Shillong Morning Teer, real archers shoot arrows at a target and the winning number is derived from the actual arrow count. That is what makes Shillong Morning Teer culturally different — it is skill-based archery, not a software-generated draw.",
  },
  {
    q: "4. Can the record of the old Morning Teer results affect the future results?",
    a: "No, previous Morning Teer results cannot decide future results. However, many followers still study the Morning Teer previous results to observe number patterns, house digits and ending digits that appear more frequently over time. It is a study reference — not a prediction.",
  },
  {
    q: "5. Why do people follow the Official Morning Teer results every day?",
    a: "People follow the Official Morning Teer results because of the traditional archery background of the game, the daily analysis of numbers, and the strong social community that discusses the results every morning. For many followers it is a daily routine, not just a number check.",
  },
  {
    q: "6. Is Morning Teer popular only in Meghalaya?",
    a: "Morning Teer originated in Shillong, Meghalaya but its popularity has spread far beyond the state. Because of the internet, followers from many regions search online every day for the Morning Teer Result Today, Common Numbers, Target Numbers and Previous Results of Shillong Morning Teer.",
  },
  {
    q: "7. What is the best way to check the official Morning Teer results?",
    a: "The safest way is to open a trusted community website like ours and read the F/R and S/R numbers directly on the homepage as soon as the counter announces them. Always cross-verify from a reliable local source before making any decision — no online site is officially operated by the counter.",
  },
  {
    q: "8. Why do players keep records of the previous Morning Teer results?",
    a: "Many players believe that studying the Morning Teer previous results helps them notice number patterns, repeating house digits and ending digits. They compare daily, weekly and monthly numbers as a study reference — it is more of a research habit than a guarantee that the same numbers will repeat.",
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg bg-orange-50/40 ring-1 ring-orange-100 mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 text-left px-3 py-2.5"
        aria-expanded={open}
        data-testid={`faq-toggle-${q.slice(0, 4)}`}
      >
        <h3 className="text-sm sm:text-base font-heading font-bold text-slate-900">
          {q}
        </h3>
        <ChevronDown
          className={`h-4 w-4 text-slate-600 shrink-0 mt-1 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <p className="text-sm text-slate-700 leading-relaxed px-3 pb-3">{a}</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Simple key-value table used inside the "Overview" and              */
/*  "Comparison" boxes.                                                */
/* ------------------------------------------------------------------ */
function DealTable({ headers, rows }) {
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th
                key={i}
                className="bg-orange-500 text-white font-heading font-black text-center px-2 py-2 border border-orange-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="bg-white">
              {r.map((c, ci) => (
                <td
                  key={ci}
                  className={`px-2 py-2 border border-slate-200 text-slate-800 ${
                    ci === 0 ? "font-bold text-center" : "text-center"
                  }`}
                >
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main SEO article                                                   */
/* ------------------------------------------------------------------ */
export default function SeoContent() {
  // FAQPage structured data
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-seo-id", "home-faq");
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q.replace(/^\d+\.\s*/, ""),
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <article className="max-w-xl mx-auto w-full px-3 sm:px-4 pb-14 mt-6">
      {/* Page header line — mirrors the reference site's H1-style intro */}
      <header className="text-center px-2 pt-3 pb-1">
        <h2 className="font-heading font-black text-slate-900 text-lg sm:text-xl leading-snug">
          Morning Sunday Teer Result &amp; Morning Teer Result | Shillong Morning
          Teer
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Welcome to the official page for the fastest Morning Teer, Shillong
          Morning Teer and Morning Sunday Teer updates.
        </p>
      </header>

      {/* Intro paragraphs */}
      <DealBox id="intro">
        <p>
          If you are interested in Shillong's teer game, then surely you also
          follow the <strong>Shillong Morning Teer Result</strong> every day.
          Checking today's <strong>Morning Teer Result</strong> has slowly become
          a part of local culture — a small routine that many players complete
          right after their morning tea.
        </p>
        <p>
          The popularity of <strong>Morning Teer</strong>,{" "}
          <strong>Shillong Morning Teer</strong> and{" "}
          <strong>Morning Sunday Teer</strong> is continuously increasing in
          Shillong because the game is not a computer-based or mobile lottery.
          It is played by real archers, and this traditional method has grown
          into one of the most followed number games in Meghalaya.
        </p>
      </DealBox>

      {/* Overview */}
      <DealBox title="Overview of Shillong Morning Teer" id="overview">
        <DealTable
          headers={["Topic", "Details"]}
          rows={[
            ["Game Type", "Traditional archery-based number game"],
            ["Popular Region", "Shillong, Meghalaya"],
            ["Result Method", "Based on arrow count"],
            ["Daily Results", "Released according to the official schedule"],
            ["Number Range", "Two-digit winning number"],
            ["Interest Level", "Very high among regular followers"],
            ["Result Tracking", "Online and offline"],
            ["Analysis", "Weekly and monthly reviews"],
          ]}
        />
        <p className="text-xs italic text-slate-500">
          This brief description gives new players an idea of why{" "}
          <strong>Shillong Morning Teer</strong> is one of the most popular
          archery-based number games in Shillong, Meghalaya.
        </p>
      </DealBox>

      {/* What is Morning Teer */}
      <DealBox title="What is Morning Teer?" id="what-is-morning-teer">
        <p>
          <strong>Morning Teer</strong> is a number game based on archery and is
          played mainly in <strong>Shillong, Meghalaya</strong>. It is not like
          a simple lottery. The winning number comes from a real archery
          competition — trained archers shoot hundreds of arrows at a target,
          officials carefully count each one, and the last two digits of the
          final total become the official <strong>Morning Teer</strong> result.
        </p>
        <p>
          This traditional method has given the game a special character. It is
          a blend of history, skill and social participation — not just a
          software-driven number draw.
        </p>
      </DealBox>

      {/* Why do people search for Morning Sunday Teer */}
      <DealBox
        title="Why do people search for Morning Sunday Teer?"
        id="why-search-morning-sunday-teer"
      >
        <p>
          Many players specifically look up <strong>Morning Sunday Teer</strong>{" "}
          on Sunday mornings. Weekends bring more free time, so followers
          compare Sunday numbers with previous days, cross-check{" "}
          <Link
            to="/common-number"
            className="text-orange-600 font-semibold underline"
          >
            common numbers
          </Link>{" "}
          and even look at{" "}
          <Link
            to="/dream-number"
            className="text-orange-600 font-semibold underline"
          >
            dream numbers
          </Link>{" "}
          on that day.
        </p>
        <p>
          For some, <strong>Morning Sunday Teer</strong> is a personal fascination
          that has continued year after year. Others simply enjoy comparing
          today's number with the previous week while drinking tea in the
          morning — a small habit that grew into a daily community routine.
        </p>
      </DealBox>

      {/* How does Morning Teer work */}
      <DealBox title="How does Morning Teer work?" id="how-morning-teer-works">
        <p>
          One of the reasons for the popularity of <strong>Morning Teer</strong>{" "}
          is that it is very simple. Experienced archers gather for a shooting
          round and start releasing arrows at a target. Once the round is over,
          officials carefully count each arrow, note the numbers that were hit
          and use the last two digits of the total as the winning number.
        </p>
        <p>
          Archers play <strong>two rounds</strong> every day — one around{" "}
          <strong>10:30 AM (F/R)</strong> and another around{" "}
          <strong>11:30 AM (S/R)</strong>. Because there are two rounds, the
          Morning Teer Result of the day always contains two winning numbers.
        </p>
      </DealBox>

      {/* Why Shillong Morning Teer is different */}
      <DealBox
        title="Why is Shillong Morning Teer different from all others?"
        id="different-from-others"
      >
        <p>
          Many lottery-based games are decided by computers or software. In the
          case of <strong>Shillong Morning Teer</strong>, real archers stand at
          the counter, shoot arrows with their own skill, and the winning number
          is derived from what those arrows actually hit. That live human
          element is what makes the game so exciting and culturally rich.
        </p>
      </DealBox>

      {/* Why results differ every day */}
      <DealBox
        title="Why do the results of the Morning Teer differ every day?"
        id="why-results-differ"
      >
        <p>
          No archer can shoot the same arrow every day. Weather changes, the
          archer's mental state on that day, wind, timing between arrows — even
          a small difference can change where an arrow lands. That is why the{" "}
          <strong>Morning Teer Result</strong> looks different every day.
        </p>
        <p>
          It is precisely this unpredictable, human element that keeps the{" "}
          <strong>Morning Teer Result</strong> interesting — there is no rule or
          software formula that can predict the outcome in advance.
        </p>
      </DealBox>

      {/* When results are published */}
      <DealBox
        title="When are the Morning Teer results published?"
        id="when-results-published"
      >
        <p>
          Morning Teer usually publishes the F/R result around{" "}
          <strong>10:30 AM</strong> and the S/R result around{" "}
          <strong>11:30 AM</strong>. These results are announced after the
          shooting session ends and the officials finish counting the arrows.
          Many followers keep a daily routine around this announcement so they
          never miss the fresh <strong>Morning Teer Result</strong>.
        </p>
      </DealBox>

      {/* Cultural significance */}
      <DealBox
        title="Cultural significance of Shillong Morning Teer"
        id="cultural-significance"
      >
        <p>
          <strong>Shillong Morning Teer</strong> is not only about numbers — it
          is deeply connected to the traditional archery of{" "}
          <strong>Meghalaya</strong>. Archery has always been respected here as
          both a cultural activity and a sport. Keeping this tradition alive
          while also reaching modern followers online is one big reason why{" "}
          <strong>Shillong Morning Teer</strong> remains so popular even today.
        </p>
      </DealBox>

      {/* How to understand numbers */}
      <DealBox
        title="How to understand the numbers of the Morning Teer Result?"
        id="understand-numbers"
      >
        <p>
          To understand the <strong>Morning Teer</strong> numbers you have to
          study them. Regular followers of the{" "}
          <strong>Morning Teer Result</strong> believe that reviewing past
          results helps them understand how the numbers behave over time.
        </p>
        <p>
          Although no game can definitely predict future results, analysing
          past records has become a popular tradition. That is why so many
          people search for the{" "}
          <Link
            to="/previous-results"
            className="text-orange-600 font-semibold underline"
          >
            Shillong Morning Teer Previous Results
          </Link>{" "}
          before every draw. Sometimes today's number looks similar to a
          previous one — but most of the time each Morning Teer Result is
          unique.
        </p>
      </DealBox>

      {/* Popular ways to analyze */}
      <DealBox
        title="Popular ways to analyse Morning Teer Results"
        id="popular-ways-to-analyse"
      >
        <p>
          When reviewing historical results, teer followers usually rely on a
          few common techniques. Many people track{" "}
          <strong>dream numbers</strong>, others study{" "}
          <strong>common numbers</strong>. Here are the most common approaches:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Check the previous results of the Morning Teer</li>
          <li>Note the repeated counter numbers of the Morning Teer</li>
          <li>Study the common numbers of the Morning Teer</li>
          <li>Match the Morning Teer dream numbers</li>
          <li>Cross-check missing numbers with previous results</li>
          <li>Follow the weekly trend</li>
        </ul>
        <p>
          Following these methods, many players analyse the{" "}
          <strong>Shillong Morning Teer</strong> results carefully, which makes
          this game even more interesting.
        </p>
      </DealBox>

      {/* Weekly Analysis */}
      <DealBox
        title="Morning Sunday Teer Weekly Analysis"
        id="weekly-analysis"
      >
        <p>
          Weekly analysis helps players observe short-term changes in the number
          pattern instead of focusing only on one day. Followers identify
          repeating numbers and compare them across the next seven days. Some
          numbers appear more than once in a week, others behave completely
          differently.
        </p>
        <p>
          Although weekly trends cannot predict future results, many players
          have made this weekly check a part of their daily routine — it is
          both fun and educational.
        </p>
      </DealBox>

      {/* Comparison */}
      <DealBox
        title="Comparison of Shillong Morning Teer with other games"
        id="comparison"
      >
        <p>
          There is not much to compare <strong>Shillong Morning Teer</strong>{" "}
          with other online number games. Most of those games are computer or
          software-based, while <strong>Shillong Morning Teer</strong> is played
          by real archers using their skill.
        </p>
        <DealTable
          headers={["Feature", "Shillong Morning Teer", "Typical Number Games"]}
          rows={[
            ["Based on Archery", "Yes", "No"],
            ["Live Event", "Yes", "Usually No"],
            ["Cultural Heritage", "Strong", "Limited"],
            ["Daily Results", "Yes", "Depends on the game"],
            ["Community Discussions", "Very Active", "Varies"],
          ]}
        />
      </DealBox>

      {/* Tips */}
      <DealBox
        title="Some tips for Morning Sunday Teer players"
        id="tips"
      >
        <p>
          If you are new to <strong>Morning Sunday Teer</strong>, there are a
          few simple things to know that will make the game more enjoyable:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Learn how the F/R and S/R results are calculated</li>
          <li>Follow a trusted community website for fast updates</li>
          <li>
            Regularly read the{" "}
            <Link
              to="/previous-results"
              className="text-orange-600 font-semibold underline"
            >
              Morning Teer Previous Results
            </Link>{" "}
            and{" "}
            <Link
              to="/common-number"
              className="text-orange-600 font-semibold underline"
            >
              Common Number
            </Link>{" "}
            page
          </li>
          <li>
            Look up traditional{" "}
            <Link
              to="/dream-number"
              className="text-orange-600 font-semibold underline"
            >
              Dream Numbers
            </Link>{" "}
            for keywords you saw in a dream
          </li>
          <li>
            Follow with curiosity — the more you observe, the more you learn
          </li>
        </ul>
      </DealBox>

      {/* Popularity */}
      <DealBox
        title="Why is Shillong Morning Teer Popular?"
        id="why-popular"
      >
        <p>
          The popularity of <strong>Shillong Morning Teer</strong> has spread
          far and wide outside <strong>Meghalaya</strong>. Followers from
          different regions search every day for the{" "}
          <strong>Morning Teer Result</strong>, the{" "}
          <strong>Shillong Morning Teer</strong> chart and the{" "}
          <strong>Morning Sunday Teer</strong> updates. The internet has made
          the game accessible, while the traditional archery method has kept it
          culturally attractive.
        </p>
        <p>
          Because of this balance between history and modern convenience,{" "}
          <strong>Shillong Morning Teer</strong> continues to grow in
          popularity across generations of followers.
        </p>
      </DealBox>

      {/* FAQs */}
      <DealBox title="FAQs About Morning Sunday Teer Result" id="faqs">
        {FAQS.map((f, i) => (
          <FAQItem key={i} q={f.q} a={f.a} />
        ))}
      </DealBox>

      {/* Final thoughts */}
      <DealBox title="Final Thoughts" id="final-thoughts">
        <p>
          The <strong>Morning Teer Result</strong> is more than a number
          announcement — it is a small window into a cultural practice. Real
          archers determine the outcome with their own hands, which makes{" "}
          <strong>Shillong Morning Teer</strong> and{" "}
          <strong>Morning Sunday Teer</strong> genuinely different from any
          software-driven number game.
        </p>
        <p>
          The current popularity of <strong>Shillong Morning Teer</strong>{" "}
          shows how deeply people value a blend of culture, history and
          everyday excitement. If you are new to the world of{" "}
          <strong>Morning Sunday Teer</strong>, take time to understand the
          rules, follow reliable Morning Teer Result updates, and enjoy the
          journey of reviewing patterns over time.
        </p>
        <p className="text-xs text-slate-500 mt-2">
          <strong>Disclaimer:</strong> This website is a community-run
          information portal. Common numbers, target numbers, house digits and
          ending digits are study references only and are{" "}
          <strong>not guaranteed</strong>. Please cross-verify from a reliable
          local source and play only where Teer is legally regulated in your
          state. Read our full{" "}
          <Link
            to="/disclaimer"
            className="text-orange-600 font-semibold underline"
          >
            Disclaimer
          </Link>{" "}
          for details.
        </p>
      </DealBox>
    </article>
  );
}
